import React, { useCallback, useEffect, useRef, useState } from 'react';

import { makeLinkVideoURL } from '../../../../../../../../services/discovery/_shared/urlMakers';
import {
    useCreatorLevel,
    useDiscoveryUIDialog,
    usePreviewAction,
    usePreviewElementId,
} from '../../../../../../selectors/index.hooks';
import {
    useFrameElementDate,
    useFrameElementMonitoredId,
    useSourceSetElementDate,
    useSourceSetElementMonitoredId,
} from '../../../../../../selectors/composed/index.hooks';

import TranslationHelper from '../../../../../../../../helpers/TranslationHelper';
import { getHlsParam } from '../../../../../../../../helpers/getHlsParam';

import { TOOLTIP_DEBOUNCE } from '../../../../../../../../constants/DebounceValues';

import VideoPlayerDialog, {
    ALL_CAMERAS_ID,
} from '../../../../../../../../components/dialogs/VideoPlayerDialog/VideoPlayerDialog';
import DialogTitle from '../../../../../../../../components/dialogs/DialogTitle/DialogTitle';
import PlayerDropdownOptions from '../../../../../../../../components/PlayerDropdownOptions/PlayerDropdownOptions';
import SelectField from '../../../../../../../../components/SelectField';
import { useAppDispatch } from '../../../../../../../../hooks';
import {
    activateDialog,
    closeDialog,
} from '../../../../../../../../state/_actions';
import { usePlayerSettings } from '../../../../../../../../state/ui/discoverySlice/index.hooks';
import { getMovieDownloadUrl } from '../../../../../_utils/getMovieDownloadUrl';
import { isEmpty } from 'lodash';
import downloadMovie from '../../../../../../../../helpers/moviePlayerOptions/downloadMovie';
import { downloadFrame } from '../../../../../../../../helpers/moviePlayerOptions/downloadFrame';
import { useSnackbar } from 'notistack';
import { CAMERAS_RANGE } from '../../../../../../../../constants/videoPlayerSettings';

const MoviePlayer = () => {
    const [copyLinkTooltip, setCopyLinkTooltip] = useState(
        TranslationHelper.translate('Copy full link to clipboard')
    );

    const dialog = useDiscoveryUIDialog();

    const { elementCollectionName, context } = dialog || {};

    const { sliderTimeRange } = dialog?.videoParams || {};

    const range = sliderTimeRange;
    const [selectedCamera, setSelectedCamera] = useState(
        elementCollectionName || ''
    );

    const [copyVideoLinkTooltip, setCopyVideoLinkTooltip] = useState(
        TranslationHelper.translate('Copy only video link to clipboard')
    );

    const playerSettings = usePlayerSettings();
    const { labels, model, confidenceThreshold, anonymize } =
        playerSettings || {};
    const previewAction = usePreviewAction();
    const ids = dialog?.videoParams?.ids || [];
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hls = getHlsParam(anonymize, labels);
    const [open, setOpen] = useState(false);
    const frameDate = useFrameElementDate();
    const eventDate = useSourceSetElementDate();
    const isInFrameContext = context === 'frameDetails';
    const date = isInFrameContext ? frameDate : eventDate;
    const elementId = usePreviewElementId();
    const creatorLevel = useCreatorLevel();

    const sourceSetElementMonitoredId = useSourceSetElementMonitoredId();
    const frameElementMonitoredId = useFrameElementMonitoredId();
    const monitoredId = sourceSetElementMonitoredId || frameElementMonitoredId;
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useAppDispatch();
    useEffect(() => {
        if (dialog && !isEmpty(ids)) {
            setOpen(true);
            setSelectedCamera(dialog.elementCollectionName);
        }
    }, [dialog]);

    useEffect(() => {
        if (open) {
            setTimeout(() => videoRef.current?.focus());
        }
    }, [open]);

    const updateVideoDialog = useCallback(
        (time: number, param: string, reportId?: string) => {
            dispatch(
                activateDialog(
                    param || '',
                    elementId,
                    'sourceSetElementMoviePlayer',
                    'preview',
                    {
                        type: 'source-set-element-movieplayer',
                        level: creatorLevel + 1,
                        videoParams: {
                            timestamp: time,
                            sliderTimeRange: range,
                            playerSettings,
                            reportId,
                            ids,
                        },
                        context,
                    }
                )
            );
        },
        [playerSettings]
    );

    const handleClosePlayerDialog = useCallback(
        (event?: object, reason?: string) => {
            if (reason === 'backdropClick') {
                return;
            }
            dialog && dispatch(closeDialog(dialog.creatorLevel));
            setOpen(false);
        },
        [dialog]
    );
    if (!dialog) {
        return <></>;
    }

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const copyLink = () => {
        videoRef.current &&
            updateVideoDialog(
                videoRef.current.currentTime,
                selectedCamera,
                dialog.videoParams?.reportId
            );
        setCopyLinkTooltip(
            TranslationHelper.translate('Full link has been copied')
        );
        setTimeout(() => copyToClipboard(window.location.href));
        setTimeout(
            () =>
                setCopyLinkTooltip(
                    TranslationHelper.translate('Copy full link to clipboard')
                ),
            TOOLTIP_DEBOUNCE
        );
    };

    const copyVideoLink = () => {
        if (date && monitoredId) {
            setCopyVideoLinkTooltip(
                TranslationHelper.translate('Video link has been copied')
            );
            setTimeout(() =>
                copyToClipboard(
                    makeLinkVideoURL({
                        timestamp: date,
                        monitoredId,
                        selectedCamera,
                        rangeMinus: range?.[0],
                        rangePlus: range?.[1],
                        anonymize,
                        hls,
                        labels,
                        model,
                        confidenceThreshold,
                    }).toString()
                )
            );
            setTimeout(
                () =>
                    setCopyVideoLinkTooltip(
                        TranslationHelper.translate(
                            'Copy only video link to clipboard'
                        )
                    ),
                TOOLTIP_DEBOUNCE
            );
        }
    };

    const handleChangeCamera = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const reportId = dialog.videoParams?.reportId;
        handleClosePlayerDialog();
        setSelectedCamera(event.target.value);
        const time = videoRef.current?.currentTime;

        setTimeout(
            () => updateVideoDialog(time || 0, event.target.value, reportId),
            100
        );
    };
    const showNotification = (success: boolean, message: string) => {
        enqueueSnackbar(message, {
            variant: success ? 'success' : 'error',
        });
    };

    const handleDownloadFrame = () => {
        if (range) {
            downloadFrame({
                videoRef,
                quality: 1,
                range,
                date,
                selectedCamera,
            });
        }
    };
    const handleDownloadMovie = async () => {
        setDownloadInProgress(true);
        downloadMovie({
            url: getMovieDownloadUrl({
                paramName: selectedCamera,
                hls: false,
                sliderTimeRange: sliderTimeRange || CAMERAS_RANGE,
                playerSettings,
                isInFrameContext,
                date,
                monitoredId,
                elementId,
            }),
            onSuccess: () => {
                setDownloadInProgress(false);
            },
            onError: () => {
                showNotification(
                    false,
                    TranslationHelper.translate(
                        'An error has occurred, please try again later'
                    )
                );
            },
        });
    };

    const isAllCamerasSelected = () => {
        return selectedCamera === ALL_CAMERAS_ID;
    };

    const downloadProps = isAllCamerasSelected()
        ? undefined
        : {
              handleDownloadFrame,
              handleDownloadMovie,
              downloadInProgress,
          };

    const renderDropdownButtons = () => {
        return (
            <div>
                <PlayerDropdownOptions
                    copyLink={
                        !previewAction?._webx?.changeSourceSetDisabled
                            ? { title: copyLinkTooltip, callback: copyLink }
                            : undefined
                    }
                    copyVideoLink={{
                        title: copyVideoLinkTooltip,
                        callback: copyVideoLink,
                    }}
                    downloadProps={downloadProps}
                />
            </div>
        );
    };

    const renderTitleButton = () => {
        return (
            <SelectField
                name="selectedCamera"
                value={selectedCamera}
                ids={ids}
                handleChange={handleChangeCamera}
                translateContext={'mp'}
            />
        );
    };
    const getSrcUrl = (paramName: string) => {
        const hls = getHlsParam(anonymize, playerSettings?.labels);
        return getMovieDownloadUrl({
            paramName,
            hls,
            sliderTimeRange: sliderTimeRange || CAMERAS_RANGE,
            playerSettings,
            isInFrameContext,
            date,
            monitoredId,
            elementId,
        });
    };
    return (
        <>
            {dialog && (
                <VideoPlayerDialog
                    getSrcUrl={playerSettings ? getSrcUrl : null}
                    videoRef={videoRef}
                    open={open}
                    handleClose={handleClosePlayerDialog}
                    type={hls ? 'application/x-mpegURL' : 'video/mp4'}
                    selectedCamera={selectedCamera}
                    ids={ids}
                >
                    <DialogTitle
                        title={`XTrack Player:`}
                        close={handleClosePlayerDialog}
                        dropDown={renderDropdownButtons}
                        titleButton={renderTitleButton}
                    />
                </VideoPlayerDialog>
            )}
        </>
    );
};

export default MoviePlayer;
